<script setup>
import { ref, computed, onBeforeUnmount } from 'vue'
import { useTranslaterStore } from '@/stores/translater'
import { useCarsStore } from '@/stores/cars'
import { useAuthStore } from '@/stores/auth'
import { useRouter } from 'vue-router'
import UTASmartCaptcha from '@/components/auth/UTASmartCaptcha.vue'

const props = defineProps({
    activate: {
        type: Boolean,
        required: true
    }
})

const router = useRouter()

const emit = defineEmits(['closeModal'])

const translaterStore   = useTranslaterStore()
const carsStore         = useCarsStore()
const authStore         = useAuthStore()

const getTranslate      = computed(() => translaterStore.getTranslate)

const carsCreateProcess     = computed(() => carsStore.carsCreapeProcess)
const carsCreateMsg         = computed(() => carsStore.carsCreateMsg)
const carsActivateProcess   = computed(() => carsStore.carsActivateProcess)
const carsActivateMsg       = computed(() => carsStore.carsActivateMsg)
const smartCaptchaId        = computed(() => authStore.smartCaptchaId)

const UTACarsCreateVin          = ref('')
const UTACarsCreateNumber       = ref('')
const UTACarsCreateImei         = ref('')
const UTACarsCreateNamiNumber   = ref('')
const UTACarsCreateName         = ref('')
const UTACarsCreatePhone        = ref('')
const UTACarsCreateState        = ref('')
const UTACarsCreateIccid        = ref('')

const formInvalids   = ref({})
const formErrors     = ref([])
const scriptErrors   = ref([])
const carsCreateData = ref({})

// Ошибки скрипта каптчи
function setScriptError(error) {

    scriptErrors.value.push(error)
}

function handleSubmit() {

    formErrors.value = []
    formInvalids.value = {}

    if(props.activate !== false) {

        carsCreateData.value = {}

        if(UTACarsCreateVin.value) {
            carsCreateData.value.vin = UTACarsCreateVin.value
        } else {
            formErrors.value.push(getTranslate.value.CARS_CREATE_VIN)
            formInvalids.value.UTACarsCreateImei = true
        }

        if(UTACarsCreateIccid.value) {
            carsCreateData.value.iccid = UTACarsCreateIccid.value
        } else {
            formErrors.value.push(getTranslate.value.CARS_CREATE_ICCID)
            formInvalids.value.UTACarsCreateIccid = true
        }

        if(!window.smartCaptcha) {
            formErrors.value.push(getTranslate.SMART_CAPTCHA_ERROR_METHOD)
            formInvalids.value.UTAAuthCaptcha = true
        }

        if(formErrors.value.length == 0) {
            if(carsActivateMsg.value?.success == false) window.smartCaptcha.reset(smartCaptchaId.value)

            window.smartCaptcha.execute(smartCaptchaId.value)
        }

    } else {

        submitCarsCreate()
    }
}

function submitCarsCreate(smartCaptchaToken) {

    if(props.activate == false) {
        
        let payload = { 
            data: {
                type: 'UAZ'
            } 
        }

        if(UTACarsCreateImei.value) {
            payload.data.imei = UTACarsCreateImei.value
        } else {
            
            formErrors.value.push(getTranslate.value.CARS_CREATE_IMEI)
            formInvalids.value.UTACarsCreateImei = true
        }

        if(UTACarsCreateVin.value)          payload.data.vin    = UTACarsCreateVin.value
        if(UTACarsCreateNumber.value)       payload.carNumber   = UTACarsCreateNumber.value
        if(UTACarsCreateNamiNumber.value)   payload.namiNumber  = UTACarsCreateNamiNumber.value
        if(UTACarsCreateName.value)         payload.name        = UTACarsCreateName.value
        if(UTACarsCreatePhone.value)        payload.data.phone  = UTACarsCreatePhone.value
        if(UTACarsCreateState.value)        payload.data.state  = UTACarsCreateState.value

        if(formErrors.value.length == 0) {
            carsStore.createCar(payload).then(newCar => {
                const newUuid = newCar.uuid
                if(newUuid) {
                    emit('closeModal', true)
                    router.push('/cars/' + newUuid)
                }
            })
        }

    } else {       

        if(typeof smartCaptchaToken === "string" && smartCaptchaToken.length > 0) {
            carsCreateData.value.captcha = {
                yandex: {
                    token: smartCaptchaToken
                }
            }
        } else {
            formErrors.value.push(getTranslate.value.AUTH_CAPTCHA)
            formInvalids.value.UTAAuthCaptcha = true
        }

        if(formErrors.value.length == 0) {
            carsStore.activateCar(carsCreateData.value)
        } else {
            window.smartCaptcha.reset(smartCaptchaId.value)
        }
    }
}

onBeforeUnmount(() => {

    if(Object.keys(carsCreateMsg.value).length) carsStore.setCarsCreateMsg({})
    if(Object.keys(carsActivateMsg.value).length) carsStore.setCarsActivateMsg({})
})
</script>

<template>
    <form class="uta-cars-create" @submit.prevent="handleSubmit">
        <div class="mb-4 uta-alert uta-alert--danger" v-if="scriptErrors.length">
            <span class="material-icons uta-alert__icon">error_outline</span>
            <div class="uta-alert__text-item" v-for="(error, index) in scriptErrors" :key="error">
                {{ error }}{{ index !== scriptErrors.length - 1 ? ', ' : '' }} 
            </div>
        </div>
        <div class="mb-4 uta-alert uta-alert--danger" v-if="formErrors.length">
            <span class="material-icons uta-alert__icon">error_outline</span>
            <div class="uta-alert__text">
                {{ getTranslate.EFormFields }}:
                <span class="uta-alert__text-item" v-for="(error, index) in formErrors" :key="error">
                    {{ error }}{{ index !== formErrors.length - 1 ? ', ' : '' }} 
                </span>
            </div>
            <button type="button" class="uta-alert__close" @click="formErrors = [], formInvalids = {}">
                <span class="material-icons">
                    close
                </span>
            </button>
        </div>
        <div class="mb-4 uta-alert uta-alert--danger" v-if="'success' in carsCreateMsg && !carsCreateMsg.success">
            <span class="material-icons uta-alert__icon">error_outline</span>
            <div class="uta-alert__text">
                {{ getTranslate[carsCreateMsg.text] || carsCreateMsg.text }}
            </div>
            <button type="button" class="uta-alert__close" @click="carsStore.setCarsCreateMsg({})">
                <span class="material-icons">
                    close
                </span>
            </button>
        </div>
        <div class="mb-4 uta-alert uta-alert--danger" v-if="'success' in carsActivateMsg && !carsActivateMsg.success">
            <span class="material-icons uta-alert__icon">error_outline</span>
            <div class="uta-alert__text">
                {{ getTranslate[carsActivateMsg.text] || carsActivateMsg.text }}
            </div>
            <button type="button" class="uta-alert__close" @click="carsStore.setCarsActivateMsg({})">
                <span class="material-icons">
                    close
                </span>
            </button>
        </div>
        <div class="row">
            <template v-if="props.activate == false">
                <div class="col-12 col-md-6">
                    <div class="uta-cars-create__group">
                        <label for="UTACarsCreateImei">
                            {{ getTranslate.CARS_CREATE_IMEI }}
                            <span class="form-required">*</span>
                        </label>
                        <input 
                            class="uta-input uta-input--block"
                            :class="{ 'uta-input--invalid' : formInvalids.UTACarsCreateImei }"
                            @input="delete formInvalids.UTACarsCreateImei"
                            type="text" 
                            id="UTACarsCreateImei"
                            v-model="UTACarsCreateImei"
                        >
                    </div>
                </div>
                <div class="col-12 col-md-6">
                    <div class="uta-cars-create__group">
                        <label for="UTACarsCreateVin">{{ getTranslate.CARS_CREATE_VIN }}</label>
                        <input 
                            class="uta-input uta-input--block"
                            :class="{ 'uta-input--invalid' : formInvalids.UTACarsCreateVin }"
                            @input="
                                $event.target.value = $event.target.value.toUpperCase(), 
                                delete formInvalids.UTACarsCreateVin
                            "
                            type="text" 
                            id="UTACarsCreateVin"
                            v-model="UTACarsCreateVin"
                        >
                    </div>
                </div>
                <div class="col-12 col-md-6">
                    <div class="uta-cars-create__group">
                        <label for="UTACarsCreateNumber">{{ getTranslate.CARS_CREATE_NUMBER }}</label>
                        <input 
                            class="uta-input uta-input--block"
                            :class="{ 'uta-input--invalid' : formInvalids.UTACarsCreateNumber }"
                            @input="delete formInvalids.UTACarsCreateNumber"
                            type="text" 
                            id="UTACarsCreateNumber"
                            v-model="UTACarsCreateNumber"
                        >
                    </div>
                </div>
                <div class="col-12 col-md-6">
                    <div class="uta-cars-create__group">
                        <label for="UTACarsCreateNamiNumber">{{ getTranslate.CARS_CREATE_NAMI_NUMBER }}</label>
                        <input 
                            class="uta-input uta-input--block"
                            :class="{ 'uta-input--invalid' : formInvalids.UTACarsCreateNamiNumber }"
                            @input="delete formInvalids.UTACarsCreateNamiNumber"
                            type="text" 
                            id="UTACarsCreateNamiNumber"
                            v-model="UTACarsCreateNamiNumber"
                        >
                    </div>
                </div>
                <div class="col-12 col-md-6">
                    <div class="uta-cars-create__group">
                        <label for="UTACarsCreateName">{{ getTranslate.CARS_CREATE_NAME }}</label>
                        <input 
                            class="uta-input uta-input--block"
                            :class="{ 'uta-input--invalid' : formInvalids.UTACarsCreateName }"
                            @input="delete formInvalids.UTACarsCreateName"
                            type="text" 
                            id="UTACarsCreateName"
                            v-model="UTACarsCreateName"
                        >
                    </div>
                </div>
                <div class="col-12 col-md-6">
                    <div class="uta-cars-create__group">
                        <label for="UTACarsCreatePhone">{{ getTranslate.CARS_CREATE_PHONE }}</label>
                        <input 
                            class="uta-input uta-input--block"
                            :class="{ 'uta-input--invalid' : formInvalids.UTACarsCreatePhone }"
                            @input="delete formInvalids.UTACarsCreatePhone"
                            type="text" 
                            id="UTACarsCreatePhone"
                            v-model="UTACarsCreatePhone"
                        >
                    </div>
                </div>
                <div class="col-12 col-md-6">
                    <div class="uta-cars-create__group">
                        <label for="UTACarsCreateState">{{ getTranslate.CARS_CREATE_STATE }}</label>
                        <input 
                            class="uta-input uta-input--block"
                            :class="{ 'uta-input--invalid' : formInvalids.UTACarsCreateState }"
                            @input="delete formInvalids.UTACarsCreateState"
                            type="text" 
                            id="UTACarsCreateState"
                            v-model="UTACarsCreateState"
                        >
                    </div>
                </div>
                <div class="col-12 col-md-6">
                    <div class="uta-cars-create__group">
                        <label for="UTACarsCreateIccid">
                            {{ getTranslate.CARS_CREATE_ICCID }} [ICC ID]
                        </label>
                        <input 
                            class="uta-input uta-input--block"
                            :class="{ 'uta-input--invalid' : formInvalids.UTACarsCreateIccid }"
                            @input="delete formInvalids.UTACarsCreateIccid"
                            type="text" 
                            id="UTACarsCreateIccid"
                            v-model="UTACarsCreateIccid"
                        >
                    </div>
                </div>
            </template>
            <template v-else>
                <div class="col-12">
                    <div class="uta-cars-create__group">
                        <label for="UTACarsCreateVin">
                            {{ getTranslate.CARS_CREATE_VIN }}
                            <span class="form-required">*</span>
                        </label>
                        <input 
                            class="uta-input uta-input--block"
                            :class="{ 'uta-input--invalid' : formInvalids.UTACarsCreateIccid }"
                            @input="
                                $event.target.value = $event.target.value.toUpperCase(), 
                                delete formInvalids.UTACarsCreateVin
                            "
                            type="text" 
                            id="UTACarsCreateVin"
                            v-model="UTACarsCreateVin"
                        >
                    </div>
                </div>
                <div class="col-12">
                    <div class="uta-cars-create__group">
                        <label for="UTACarsCreateIccid">
                            {{ getTranslate.CARS_CREATE_ICCID }}
                            <span class="form-required">*</span>
                        </label>
                        <input 
                            class="uta-input uta-input--block"
                            :class="{ 'uta-input--invalid' : formInvalids.UTACarsCreateIccid }"
                            @input="delete formInvalids.UTACarsCreateIccid"
                            type="text" 
                            id="UTACarsCreateIccid"
                            v-model="UTACarsCreateIccid"
                        >
                    </div>
                </div>
                <div class="col-12">
                    <div class="uta-cars-create__group">
                        <UTASmartCaptcha @getToken="submitCarsCreate" @error="setScriptError" />
                    </div>
                </div>
            </template>
        </div>
        <div class="form-descr">
                <span class="form-required">⚹</span> - {{ getTranslate.FORM_REQUIRED }}
            </div>
        <div class="uta-btns">
            <button type="submit" class="uta-btn uta-btn--main">
                <template v-if="carsCreateProcess || carsActivateProcess">
                    ...
                </template>
                <template v-if="props.activate">
                    {{ getTranslate.BTN_ADD }}
                </template>
                <template v-else>
                    {{ getTranslate.BTN_CREATE }}
                </template>
            </button>
        </div>
    </form>
</template>

<style lang="scss">
@import '@/assets/styles/scss/components/cars-create';
</style>